<template>
    <div>
        <div class="community-container">
            <div class="container-fluid opener-section">
                <div class="d-sm-flex justify-content-center opener-container">
                    <div class="text-sm-center">
                        <div class="hero-text">
                            <h1 class="text-brand community-intro">
                                <span class="icon align-middle icon-glyphicons-basic-world-east mr-1"></span>
                                <span class="align-middle"
                                    >Explore freely-usable learning experiences from leading institutions and
                                    instructors</span
                                >
                            </h1>
                            <p class="hero-description hero-section">
                                Discover impactful learning experiences in your profession,
                                <br v-if="!userIsAnonymous" />freely-usable under
                                <a :href="creativeCommonsLicenseUrl" rel="noopener" target="_blank">Creative Commons</a
                                >.
                                <span v-if="userIsAnonymous">
                                    To track your progress,
                                    <a href="/community/get-started" target="_blank" rel="noopener">
                                        create an account</a
                                    >.
                                </span>
                            </p>
                            <div class="d-flex justify-content-sm-center mb-4 mb-sm-0 pt-1">
                                <ul class="d-flex category-tag-container">
                                    <li
                                        class="category-tag"
                                        :class="{ active: currentCategoryName === null }"
                                        @click="viewCategoryByName(null)"
                                        role="button"
                                        aria-label="Reset category filter"
                                        tabindex="0"
                                        @keyup.enter="viewCategoryByName(null)"
                                    >
                                        <a title="All">All</a>
                                    </li>
                                    <li
                                        v-for="category in categories"
                                        :key="category.id"
                                        class="category-tag"
                                        :class="{ active: currentCategoryName === category.name }"
                                        @click="viewCategoryByName(category.name)"
                                        @keyup.enter="viewCategoryByName(category.name)"
                                        role="button"
                                        :aria-label="'Filter by category ' + category.name"
                                        tabindex="0"
                                    >
                                        <a :title="category.name">{{ category.name }}</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="container-fluid community-content">
                <div class="d-flex justify-content-center">
                    <ul class="grid flex-grow-1 justify-content-center">
                        <li class="d-flex flex-column item" v-for="caseObj in cases" :key="caseObj.id">
                            <community-case-card :case-obj="caseObj"></community-case-card>
                        </li>
                    </ul>
                </div>
                <div>
                    <div v-show="loadingMore && !loading">
                        <div class="d-flex justify-content-center align-items-end community-loading-container">
                            <div class="d-flex justify-content-center align-items-center mb-2">
                                <div class="mr-3">
                                    <div class="spinner-grow spinner-grow-sm text-brand" role="status">
                                        <span class="sr-only">Loading...</span>
                                    </div>
                                </div>
                                <div>
                                    <p class="mb-0 text-secondary community-text-body">Loading More...</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <info-footers />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import InfoFooters from "@/components/InfoFooters"
import CommunityCaseCard from "@/components/CommunityCaseCard"
import Utilities from "@/assets/js/utilities.js"
import http from "@/axios.js"
import { parseISO } from "date-fns"
import differenceInDays from "date-fns/differenceInDays"
import debounce from "lodash.debounce"

export default {
    name: "CommunityLibrary",
    components: {
        InfoFooters,
        CommunityCaseCard,
    },
    data() {
        return {
            cases: [],
            categories: [],
            currentCategoryName: null,
            selectedCase: {},
            newCase: {},
            modalLoading: false,
            textFilter: "",
            tagFiltersSelected: [],
            tagSearch: "",
            currentPage: 1,
            sortColumn: { name: null },
            sortDescend: false,
            search: "",
            sortField: "last_modified",
            sortDescending: true,
            accessors: [],
            newAccessorEmail: null,
            selectedSession: null,
            copyLinkSuccessful: false,
            searchIsActive: false,
            searchText: "",
            infiniteScrollListener: null,
            loadIndex: 0,
            loadingMore: true,
            allLoaded: false,
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        userIsAnonymous() {
            return this.$store.getters.userIsAnonymous
        },
        // Used to control the minimum height of the case list - if not set,
        // the window height changes upon filtering which feels janky.
        caseListContainerStyle() {
            return {
                "min-height": String(this.cases.length * 200) + "px",
            }
        },
        creativeCommonsLicenseUrl() {
            return this.$store.getters.creativeCommonsLicenseUrl
        },
    },
    methods: {
        // Fetch initial data for the view.
        async fetchData() {
            let vm = this
            this.$store.dispatch("addLoad")
            this.loadMore().finally(function () {
                vm.$store.dispatch("removeLoad")
            })
            this.$store.dispatch("addLoad")
            http.get("/api/community/categories/")
                .then(function (response) {
                    vm.categories = response.data
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        // Load more cases into the library - used for infinite scrolling behaviour.
        loadMore(replaceExisting = false) {
            if (replaceExisting) {
                this.allLoaded = false
                this.loadIndex = 0
            }
            if (this.allLoaded) {
                return
            }
            this.loadingMore = true
            let startIndex = this.loadIndex
            this.loadIndex += 8
            let vm = this
            return http
                .get("/api/community/cases/browse/", {
                    params: {
                        startIndex: startIndex,
                        endIndex: this.loadIndex,
                        categoryName: this.currentCategoryName,
                    },
                })
                .then(function (response) {
                    // If the length of received results is less than the requested
                    // length of results, assume there are no more items to load.
                    if (response.data.length < vm.loadIndex - startIndex) {
                        vm.allLoaded = true
                    }

                    if (replaceExisting) {
                        vm.cases = response.data
                    } else {
                        vm.cases = vm.cases.concat(response.data)
                    }

                    vm.$nextTick(() => {
                        $('[data-toggle="popover"]').popover()
                    })
                })
                .finally(() => (vm.loadingMore = false))
        },
        // Determine whether a case is recently published or not - using 90 days for now.
        caseIsNew(caseObj) {
            if (!caseObj.community_published_datetime) {
                return false
            }
            let parsedDate = parseISO(caseObj.community_published_datetime)
            return differenceInDays(new Date(), parsedDate) < 90
        },
        viewCategoryByName(categoryName) {
            if (this.currentCategoryName == categoryName) {
                return
            }
            this.currentCategoryName = categoryName

            // Store the current category being viewed, so that we can route
            // the learner back to it later on.
            this.$store.commit("setCommunityLibraryCategory", categoryName)

            // Add the category query parameter while preserving any existing query parameters
            // in the URL.
            let query = {
                ...this.$route.query,
            }
            if (categoryName) {
                query.tag = categoryName
            } else {
                delete query.tag
            }
            this.$router.push({
                path: this.$route.path,
                query: query,
            })
            let vm = this
            this.$store.dispatch("addLoad")
            this.loadMore(true).then(() => {
                vm.$nextTick(() => vm.$store.dispatch("removeLoad"))
            })
        },
        navigateToScenario(scenario) {
            this.$router.push({ name: "CommunityCaseDetail", params: { id: scenario.id } })
        },
        filterOptions(option, label, search) {
            let searchLower = search.toLowerCase()
            let matchedName = option.community_title.toLowerCase().indexOf(searchLower) > -1
            if (matchedName) {
                return true
            }
            let matchedDescription = option.community_description.toLowerCase().indexOf(searchLower) > -1
            if (matchedDescription) {
                return true
            }
            let publishersJoined = option.contributors.map((c) => c.publisher.name).join(",")
            let publishersMatched = publishersJoined.toLowerCase().indexOf(searchLower) > -1
            return publishersMatched
        },
        setSearchFocusState(state) {
            this.searchIsActive = state
        },
        // Checks whether the user has permission to view a library item. This is used when the
        // add-to-parent parameter is present in the URL, which is used when adding community
        // cases to an organization's library.
        async checkLibraryItemPermissions(libraryItemId) {
            if (!libraryItemId) {
                return
            }
            await this.$store.dispatch("fetchAuth")
            if (this.userIsAnonymous && this.$route.query.addToParent) {
                // The user was probably sent the URL by someone with access to the parent
                // library item, so just remove the query parameter.
                this.clearQueryParameter("addToParent")
                return
            }
            let vm = this
            http.get(`/api/library-items/${libraryItemId}/check-permissions/?accessType=owner`).then((response) => {
                if (!response.data.has_permission) {
                    vm.clearQueryParameter("addToParent")
                }
            })
        },
        // Clear a query parameter from the current URL.
        clearQueryParameter(param) {
            let newQuery = { ...this.$route.query }
            if (param in newQuery) {
                delete newQuery[param]
                this.$router.replace({ query: newQuery })
            }
        },
    },
    created() {
        if (this.$route.query.tag) {
            // Find the category associated with the name in the query string.
            this.currentCategoryName = this.$route.query.tag
        }
        this.fetchData()
    },
    mounted() {
        // Initialize infinite scrolling behaviour.
        let vm = this
        let rootApp = document.getElementById("app")
        this.infiniteScrollListener = debounce(() => {
            let atBottom = document.documentElement.scrollTop + window.innerHeight >= rootApp.offsetHeight - 800
            if (atBottom && !vm.loadingMore && !vm.allLoaded) {
                vm.loadMore()
            }
        }, 30)
        this.checkLibraryItemPermissions(this.$route.query.addToParent)
        window.addEventListener("scroll", this.infiniteScrollListener)
    },
    beforeDestroy() {
        // Kill infinite scroll event listener.
        window.removeEventListener("scroll", this.infiniteScrollListener)
        // Kill all popovers.
        Utilities.destroyAllPopovers()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/community.scss";

.opener-section {
    max-width: 1450px;
}

.opener-container {
    margin-bottom: 50px;
    margin-top: 40px;
    @media (min-width: $breakpoint-lg) {
        margin-bottom: 50px;
        margin-top: 45px;
    }
}

.hero-text {
    max-width: 560px;
}

.hero-description {
    line-height: 1.8;
    margin-bottom: 2rem;
}

.community-intro {
    margin-bottom: 1.75rem;
    margin-top: 1rem;
    font-size: 22px;
    font-family: "Rubik", sans-serif;
    line-height: 1.6;
}

.community-container {
    margin-left: 8px;
    margin-right: 8px;
    @media (min-width: $breakpoint-lg) {
        margin-left: 50px;
        margin-right: 50px;
    }
    @media (min-width: $breakpoint-md) {
        margin-left: 22px;
        margin-right: 22px;
    }
}

.community-content {
    max-width: 1200px;
    @media (min-width: 1420px) {
        max-width: 1550px;
    }
}

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
    grid-template-rows: 1fr;
    padding-inline-start: 0px;
    grid-gap: 30px;
    @media (min-width: $breakpoint-xl) {
        grid-gap: 36px;
    }
    @media (min-width: 1550px) {
        grid-gap: 38px;
    }
}

.item {
    list-style-type: none;
}

.prelaunch-container {
    @media (min-width: $breakpoint-lg) {
        height: 85vh;
    }
    @media (min-width: $breakpoint-sm) {
        margin-left: 20px;
        margin-right: 20px;
    }
}

ul {
    margin-bottom: 0px;
    padding-inline-start: 0px;
    li {
        list-style-type: none;
    }
}

.category-tag-container {
    @media (max-width: $breakpoint-sm) {
        overflow-x: scroll;
    }
}

.community-loading-container {
    height: 120px;
}

.footer-text {
    font-weight: 700;
    font-size: 10px;
    text-transform: uppercase;
}
</style>
